import React, { useState, useEffect } from "react";
import { Table, Button, Input, InputNumber, Tabs, Card, message, Spin } from "antd";
import axios from "axios";

const { TabPane } = Tabs;

const PMMeanStdDev = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
    const [data, setData] = useState([]);
    const [config, setConfig] = useState(initialConfig || {});
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("input");

    // Initialize data based on config
    useEffect(() => {
        if (initialConfig && !fileId) {
            const initialRows = Array.from({ length: initialConfig.number_of_activities }, (_, i) => ({
                key: i,
                name: `Activity ${i + 1}`,
                activity_time: 0,
                std_dev: 0,
                predecessors: "",
                start_node: null,
                end_node: null,
            }));
            setData(initialRows);
            setConfig(initialConfig);
        } else if (fileId) {
            loadFile(fileId);
        }
    }, [fileId, initialConfig]);

    const loadFile = async (id) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`/api/retrieve_PM_mean_stddev/${id}/`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const { input_data, output_data, chart_url } = response.data;

            // Transform input_data.activities back to table format
            const loadedData = input_data.activities.map((act, index) => ({
                key: index,
                ...act,
                predecessors: Array.isArray(act.predecessors) ? act.predecessors.join(",") : act.predecessors
            }));

            setData(loadedData);
            setConfig({
                number_of_activities: input_data.number_of_activities,
                structure: input_data.structure === "immediate" ? "1" : (input_data.structure === "start/end" ? "2" : input_data.structure)
            });

            setResults({ output_data, chart_url });
            setActiveTab("output");
        } catch (error) {
            message.error("Failed to load file");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("access_token");

            // Prepare payload
            const payload = {
                name: fileName,
                input_data: {
                    number_of_activities: data.length,
                    structure: config.structure,
                    activities: data.map(item => ({
                        name: item.name,
                        activity_time: parseFloat(item.activity_time) || 0,
                        std_dev: parseFloat(item.std_dev) || 0,
                        ...(config.structure === "1"
                            ? { predecessors: item.predecessors ? item.predecessors.toString().split(",").map(s => s.trim()) : [] }
                            : { start_node: parseInt(item.start_node) || 0, end_node: parseInt(item.end_node) || 0 }
                        )
                    }))
                }
            };

            let response;
            if (fileId) {
                response = await axios.put(`/api/update_PM_mean_stddev/${fileId}/`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                response = await axios.post("/api/save_PM_mean_stddev/", payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                // Update selected file ID to switch to update mode
                if (setSelectedFile) {
                    setSelectedFile(prev => ({ ...prev, id: response.data.id }));
                }
            }

            setResults(response.data);
            setActiveTab("output");
            message.success("Analysis saved successfully");
        } catch (error) {
            console.error("Save error: ", error.response?.data);
            message.error(error.response?.data?.error || "Failed to save analysis");
        } finally {
            setLoading(false);
        }
    };

    const updateRow = (index, field, value) => {
        const newData = [...data];
        newData[index][field] = value;
        setData(newData);
    };

    // Define Columns
    const columns = [
        {
            title: "Activity Name",
            dataIndex: "name",
            key: "name",
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => updateRow(index, "name", e.target.value)} />
            ),
        },
        {
            title: "Activity Time",
            dataIndex: "activity_time",
            key: "activity_time",
            render: (text, record, index) => (
                <InputNumber min={0} value={text} onChange={(val) => updateRow(index, "activity_time", val)} />
            ),
        },
        {
            title: "Standard Deviation",
            dataIndex: "std_dev",
            key: "std_dev",
            render: (text, record, index) => (
                <InputNumber min={0} value={text} onChange={(val) => updateRow(index, "std_dev", val)} />
            ),
        },
    ];

    if (config.structure === "1") {
        columns.push({
            title: "Predecessors (comma separated)",
            dataIndex: "predecessors",
            key: "predecessors",
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => updateRow(index, "predecessors", e.target.value)} />
            ),
        });
    } else {
        columns.push(
            {
                title: "Start Node",
                dataIndex: "start_node",
                key: "start_node",
                render: (text, record, index) => (
                    <InputNumber min={0} value={text} onChange={(val) => updateRow(index, "start_node", val)} />
                ),
            },
            {
                title: "End Node",
                dataIndex: "end_node",
                key: "end_node",
                render: (text, record, index) => (
                    <InputNumber min={0} value={text} onChange={(val) => updateRow(index, "end_node", val)} />
                ),
            }
        );
    }

    // Output Charts Helper
    const renderCharts = () => {
        if (!results || !results.chart_url) return null;
        const charts = results.chart_url; // This is a dict of base64 strings

        return (
            <Tabs
                defaultActiveKey="precedence"
                items={[
                    {
                        key: "precedence",
                        label: "Precedence Graph",
                        children: <img src={charts.precedence} alt="Precedence Graph" style={{ maxWidth: "100%" }} />
                    },
                    {
                        key: "gantt_early",
                        label: "Gantt (Early)",
                        children: <img src={charts.gantt_early} alt="Gantt Early" style={{ maxWidth: "100%" }} />
                    },
                    {
                        key: "gantt_late",
                        label: "Gantt (Late)",
                        children: <img src={charts.gantt_late} alt="Gantt Late" style={{ maxWidth: "100%" }} />
                    },
                    {
                        key: "gantt_early_late",
                        label: "Gantt (Both)",
                        children: <img src={charts.gantt_early_late} alt="Gantt Both" style={{ maxWidth: "100%" }} />
                    }
                ]}
            />
        );
    };

    const outputColumns = [
        { title: "Activity", dataIndex: "name", key: "name" },
        { title: "Time", dataIndex: "activity_time", key: "activity_time" },
        { title: "Std Dev", dataIndex: "std_dev", key: "std_dev" },
        { title: "ES", dataIndex: "ES", key: "ES" },
        { title: "EF", dataIndex: "EF", key: "EF" },
        { title: "LS", dataIndex: "LS", key: "LS" },
        { title: "LF", dataIndex: "LF", key: "LF" },
        { title: "Slack", dataIndex: "slack", key: "slack" },
    ];

    return (
        <Spin spinning={loading}>
            <Card title={fileName || "Project Management (Mean & Std Dev)"}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: "input",
                            label: "Data Input",
                            children: (
                                <Table
                                    dataSource={data}
                                    columns={columns}
                                    pagination={false}
                                    rowKey="key"
                                    footer={() => <Button type="primary" onClick={handleSave}>Solve</Button>}
                                />
                            )
                        },
                        {
                            key: "output",
                            label: "Results",
                            disabled: !results,
                            children: results && (
                                <>
                                    <div style={{ marginBottom: 20 }}>
                                        <h3>Project Statistics</h3>
                                        <p><b>Project Mean Time:</b> {results.output_data.project_mean_time}</p>
                                        <p><b>Project Standard Deviation:</b> {results.output_data.project_standard_deviation}</p>
                                    </div>

                                    <h3 style={{ marginTop: 20 }}>Activity Schedule</h3>
                                    <Table
                                        dataSource={results.output_data.activities}
                                        columns={outputColumns}
                                        pagination={false}
                                        rowKey="name"
                                    />

                                    <h3 style={{ marginTop: 30 }}>Charts</h3>
                                    {renderCharts()}
                                </>
                            )
                        }
                    ]}
                />
            </Card>
        </Spin>
    );
};

export default PMMeanStdDev;
